// What a confirmation card does when the coach taps Confirm: the app's own
// code for that change, under the coach's session. The assistant only ever
// proposed it (server mcp/assistantProposals.mjs).
//
// Each resolves to { label, detail?, path?, pathLabel? }: the card's "done"
// line, what the assistant is told, and where the coach can see the result.
// Each throws an Error whose message the card shows.

import { supabase } from '@/lib/supabase';
import { createPoll } from '@/services/pollService';
import { buildNewMemberRow } from '@/utils/addingTeamMembersUtils';
import { emitAssistantDataChanged } from '@/utils/assistantDataEvents';
import { normalizePersonName, toTitleCase } from '@/utils/stringUtils';

const isDuplicateEmailError = (error) =>
  error?.code === '23505' || /uniq_team_member_email_per_team|duplicate key/i.test(error?.message || '');

async function confirmPoll(proposal) {
  // The deadline is the coach's wall clock, read on this device: what the
  // Polls page's form does with its date and time fields.
  const expires = new Date(`${proposal.closes?.date}T${proposal.closes?.time}`);
  if (Number.isNaN(expires.getTime())) throw new Error('The voting deadline on this card is not a real date. Ask the assistant again.');
  if (expires <= new Date()) throw new Error('The voting deadline on this card has passed. Ask the assistant for a new one.');
  const poll = await createPoll({
    teamId: proposal.team_id,
    title: proposal.title,
    description: proposal.description || '',
    expiresAt: expires.toISOString(),
    allowMultiple: proposal.allowMultiple === true,
    allowSuggestions: proposal.allowSuggestions === true,
    anonymous: proposal.anonymous === true,
    targetAudience: 'all',
    options: proposal.options,
  });
  emitAssistantDataChanged('polls');
  return {
    label: `Poll "${proposal.title}" created`,
    detail: `The poll "${proposal.title}" now exists${poll?.id ? ` (poll id ${poll.id})` : ''}. It has not been shared with the team yet.`,
    path: '/polls',
    pathLabel: 'Open Polls',
  };
}

async function confirmRosterAdd(proposal) {
  const row = buildNewMemberRow({
    teamId: proposal.team_id,
    name: proposal.member?.full_name,
    email: proposal.member?.email,
    role: proposal.member?.role,
    allergies: proposal.member?.allergies,
  });
  const { error } = await supabase.from('team_members').insert(row).select('id').single();
  if (error) {
    throw new Error(
      isDuplicateEmailError(error)
        ? `${row.email} is already on your team.`
        : 'Couldn’t add this member. Check your connection and try again.',
    );
  }
  emitAssistantDataChanged('members');
  return {
    label: `${row.full_name} added to the roster`,
    detail: `${row.full_name} is now on the roster as a ${row.role}${row.allergies ? `, allergies: ${row.allergies}` : ''}.`,
    path: '/team-members-management',
    pathLabel: 'Open Team',
  };
}

async function confirmRosterUpdate(proposal) {
  const changes = proposal.changes || {};
  const patch = {
    ...(changes.full_name != null ? { full_name: normalizePersonName(changes.full_name) } : {}),
    ...(changes.role != null ? { role: String(changes.role).toLowerCase() } : {}),
    ...(changes.allergies != null ? { allergies: toTitleCase(changes.allergies) } : {}),
  };
  if (Object.keys(patch).length === 0) throw new Error('This card has nothing to change.');
  // Only the row the database says it changed counts: an update RLS refuses
  // is not an error, it matches nothing.
  const { data, error } = await supabase
    .from('team_members')
    .update(patch)
    .eq('team_id', proposal.team_id)
    .eq('id', proposal.member_id)
    .select('id, full_name, role, allergies');
  if (error) throw new Error('Couldn’t save this member. Check your connection and try again.');
  const saved = data?.[0];
  if (!saved) throw new Error('That member could not be changed. They may have been removed from the team.');
  emitAssistantDataChanged('members');
  return {
    label: `${saved.full_name} updated`,
    detail: `${saved.full_name} is now: role ${saved.role}, allergies ${saved.allergies || 'none'}.`,
    path: '/team-members-management',
    pathLabel: 'Open Team',
  };
}

async function confirmSavedLocation(proposal, { userId, contact, choice }) {
  const candidates = Array.isArray(proposal.candidates) ? proposal.candidates : [];
  const candidate = candidates.find((entry) => entry.placeId === choice) || (candidates.length === 1 ? candidates[0] : null);
  if (!candidate) throw new Error('Pick the right address first.');

  // Loaded when a location card is confirmed: the module reads the auth
  // context, which the chat's own context is loaded beside.
  const { addProviderAddressInPlace, loadTeamDeliveryLocations } = await import('@/hooks/useProviderSavedAddresses');

  // The address pickers' add: saves the address for delivery and makes the
  // team's row for it. It hands back the team's existing row when that
  // address is already saved, so the rows it had before tell the two apart.
  const before = new Set(((await loadTeamDeliveryLocations(proposal.team_id)) || []).map((location) => location.id));
  const row = await addProviderAddressInPlace(candidate, {
    teamId: proposal.team_id,
    userId,
    subpremise: proposal.unit || '',
    details: {
      delivery_notes: proposal.deliveryNotes || '',
      contact_name: contact?.full || '',
      contact_phone: contact?.phone || '',
    },
  });
  if (!row?.id) throw new Error('The address could not be saved. Try again in a moment.');
  if (before.has(row.id)) {
    emitAssistantDataChanged('locations');
    return {
      label: `Already saved as "${row.address_name}"`,
      detail: `That address was already one of the team's saved locations, named "${row.address_name}". Nothing new was added and it was not renamed.`,
      path: '/saves?tab=locations',
      pathLabel: 'Open Saves',
    };
  }

  // The pickers name a new row by its street; the coach gave this one a name.
  const addressName = toTitleCase(proposal.addressName || '');
  const unit = String(proposal.unit || '').trim();
  const { error } = await supabase
    .from('saved_locations')
    .update({
      address_name: addressName || row.address_name,
      venue_type: proposal.venueType || 'main',
      venue_context: proposal.venueContext || 'home',
      ...(unit ? { metadata: { ...(row.metadata || {}), subpremise: unit } } : {}),
    })
    .eq('id', row.id);
  emitAssistantDataChanged('locations');
  if (error) {
    // The address is saved and deliverable; only its label is the street's.
    return {
      label: `Saved as "${row.address_name}"`,
      detail: `The address was saved and is deliverable, but under the name "${row.address_name}": naming it "${addressName}" failed. The coach can rename it on the Saves page.`,
      path: '/saves?tab=locations',
      pathLabel: 'Open Saves',
    };
  }
  return {
    label: `"${addressName || row.address_name}" saved`,
    detail: `"${addressName || row.address_name}" (${row.formatted_address}) is now a saved location and is deliverable.`,
    path: '/saves?tab=locations',
    pathLabel: 'Open Saves',
  };
}

const EXECUTORS = {
  poll: confirmPoll,
  roster_add: confirmRosterAdd,
  roster_update: confirmRosterUpdate,
  saved_location: confirmSavedLocation,
};

/**
 * Make the change a card proposes.
 *   context  { userId, contact (the signed-in coach's { full, phone }),
 *            choice (a saved location's picked candidate placeId) }
 */
export async function executeProposal(proposal, context = {}) {
  const run = EXECUTORS[proposal?.kind];
  if (!run) throw new Error('This card is from an older version of the app. Ask the assistant again.');
  if (!proposal.team_id) throw new Error('This card has no team. Ask the assistant again.');
  return run(proposal, context);
}
